import { useSelector } from 'react-redux';
import { VIDEO_REMOVE_BG_USE_CREDIT } from '../redux/slices/FJSliceVideoBgRemover';
import FJLocalStore from '../store/FJLocalStore';
import FJAppConstant from '../constants/FJAppConstant';
import { IMAGE_TO_IMAGE_USE_CREDIT } from '../redux/slices/FJSliceImageToImage';
import { IMAGE_ENHANCER_USE_CREDIT } from '../redux/slices/FJSliceImageEnhancer';
import { IMAGE_EXTENDER_USE_CREDIT } from '../redux/slices/FJSliceImageExtender';
import { useMemo } from 'react';
import FJTextToImageDataConfig from '../config/dataConfig/FJTextToImageDataConfig';
import { AI_AUDIO_USE_CREDIT } from '../redux/slices/FJSliceAIAudioGenerator';
import FJConstant from '../constants/FJConstant';
import { TEXT_TO_VIDEO_PRICE } from '../config/dataConfig/FJTextToVideoDataConfig';
import { AI_VIDEO_DUB_USE_CREDIT } from '../redux/slices/FJSliceAiVideoDub';
import FJTextBasedEditingConfig from '../config/appConfig/FJTextBasedEditingConfig';
import { STYLE_LIST } from '../config/dataConfig/FJImageToImageData.js';
import {
    AI_GENERATOR_VIDEO_TAB_TYPE,
    AI_VIDEO_GENERATION_MODEL_LIST,
} from '../config/dataConfig/FJAIVideoGeneratorData.js';
import { OBJECT_ERASE_USE_CREDIT } from '../redux/slices/FJSliceObjectErase.js';
import { AI_PHOTO_EDITOR_MODELS } from '../config/dataConfig/FJAIPhotoEditorData';

const useUsageDetailContainer = () => {
    const totalTime = useSelector(state => state.videoBgRemover.totalTime);
    const appType = useSelector(state => state.common.appType);
    const imageToImageGenerateQuantity = useSelector(state => state.imageToImage.generateQuantity);
    const imageEnhancerGenerateQuantity = useSelector(state => state.imageEnhancer.uploadImageList.length);
    const imageExtenderGenerateQuantity = useSelector(state => state.imageExtender.generateNumber);
    const textToImageGenerateQuantity = useSelector(state => state.textToImage.generateNumber);
    const textToImageResolution = useSelector(state => state.textToImage.curResolution);
    const aiPhotoEditorResolution = useSelector(state => state.aiPhotoEditor.curResolution);
    const textToImageCurModel = useSelector(state => state.textToImage.curModel);
    const videoGeneratorCurrentDuration = useSelector(state => state.AIVideoGenerator.currentDuration);
    const videoGeneratorAiSfxTotalCredit = useSelector(state => state.AIVideoGenerator.aiSfxTotalCredit);
    const currentVideoResolution = useSelector(state => state.AIVideoGenerator.currentResolution);
    const videoGeneratorCurrentModel = useSelector(state => state.AIVideoGenerator.currentModel);
    const tabIndex = useSelector(state => state.AIVideoGenerator.tabIndex);
    const videoEffectUploadVideoInfo = useSelector(state => state.AIVideoGenerator.videoEffectUploadVideoInfo);
    const videoEffectCredit = useSelector(state => state.AIVideoGenerator.videoEffectCredit);
    const videoEffectStyleSelectId = useSelector(state => state.AIVideoGenerator.videoEffectStyleSelectId);

    const selectTab = useSelector(state => state.textToVideo.selectTab);
    const voiceSettingActive = useSelector(state => state.textToVideo.voiceSettingActive);
    const mediaSource = useSelector(state => state.textToVideo.mediaSource);
    const textToVideoTotalCredit = useSelector(state => state.textToVideo.totalCredit);
    const aiVideoDubTotalCredit = useSelector(state => state.aiVideoDub.useCredit);
    const aiVideoDubTrimInfo = useSelector(state => state.aiVideoDub.trimInfo);
    const textBasedEditingDuration = useSelector(state => state.textBasedEditing.originalResourceData.duration);
    const videoGeneratorActiveAiSFX = useSelector(state => state.AIVideoGenerator.audioActive);
    const selectedStyle = useSelector(state => state.imageToImage.selectedStyle);
    const objectEraseCurMenuIndex = useSelector(state => state.objectErase.curMenuIndex);
    const headshotGeneratorTotalOutput = useSelector(state => state.headshotGenerator.totalOutput);
    const headshotGeneratorUnitPrice = useSelector(state => state.headshotGenerator.unitPrice);
    const headshotGeneratorCategory = useSelector(state => state.headshotGenerator.selectedCategoryKey);
    const aiPhotoEditorCurModel = useSelector(state => state.aiPhotoEditor.curModel);

    const textToImageUseCredit = useMemo(() => {
        const textToImageModel = FJTextToImageDataConfig.MODELS.find(item => item.value === textToImageCurModel);

        const modelUseCredit =
            (textToImageModel?.creditPriceLevels && textToImageModel?.creditPriceLevels?.[textToImageResolution]) ||
            textToImageModel?.useCredit ||
            0;

        const useCredit = modelUseCredit * textToImageGenerateQuantity;

        return useCredit;
    }, [textToImageCurModel, textToImageGenerateQuantity, textToImageResolution]);

    const getDataList = () => {
        switch (appType) {
            case FJAppConstant.VIDEO_REMOVE_BG: {
                const label1 = 'remove-background';
                const value1 = `${VIDEO_REMOVE_BG_USE_CREDIT} ${FJLocalStore._('credits')}/s`;
                const label2 = 'duration';
                const value2 = `${Math.floor(totalTime)} s`;
                const totalCredit = (Math.floor(totalTime) * VIDEO_REMOVE_BG_USE_CREDIT).toFixed(1);

                return {
                    list: [
                        [
                            { label: label1, value: value1 },
                            { label: label2, value: value2 },
                        ],
                    ],
                    totalCredit,
                };
            }
            case FJAppConstant.IMAGE_TO_IMAGE: {
                const category = STYLE_LIST.find(ca => ca.list.find(item => item.value === selectedStyle));
                const styleInfo = category ? category.list.find(item => item.value === selectedStyle) : {};

                // 单张消耗积分
                const consumePointsPerTicket =
                    IMAGE_TO_IMAGE_USE_CREDIT * (styleInfo.isPro || styleInfo.isExpensive ? 2 : 1);
                const label1 = 'image-to-image';
                const value1 = `${FJLocalStore._(consumePointsPerTicket === 1 ? 'use-credit' : 'use-credits').replace('xxx', consumePointsPerTicket)}`;
                const label2 = 'count';
                const value2 = `${imageToImageGenerateQuantity}`;
                const totalCredit = (imageToImageGenerateQuantity * consumePointsPerTicket).toFixed(1);

                return {
                    list: [
                        [
                            { label: label1, value: value1 },
                            { label: label2, value: value2 },
                        ],
                    ],
                    totalCredit: totalCredit,
                };
            }
            case FJAppConstant.IMAGE_ENHANCER: {
                const label1 = 'upscale';
                const value1 = `${FJLocalStore._(IMAGE_ENHANCER_USE_CREDIT === 1 ? 'use-credit' : 'use-credits').replace('xxx', IMAGE_ENHANCER_USE_CREDIT)}`;
                const label2 = 'count';
                const value2 = `${Math.max(imageEnhancerGenerateQuantity, 0)}`;
                const totalCredit = (Math.max(imageEnhancerGenerateQuantity, 0) * IMAGE_ENHANCER_USE_CREDIT).toFixed(1);

                return {
                    list: [
                        [
                            { label: label1, value: value1 },
                            { label: label2, value: value2 },
                        ],
                    ],
                    totalCredit: totalCredit,
                };
            }
            case FJAppConstant.IMAGE_EXTENDER: {
                const label1 = 'extend';
                const value1 = `${FJLocalStore._(IMAGE_EXTENDER_USE_CREDIT === 1 ? 'use-credit' : 'use-credits').replace('xxx', IMAGE_EXTENDER_USE_CREDIT)}`;
                const label2 = 'count';
                const value2 = `${Math.max(imageExtenderGenerateQuantity, 0)}`;
                const totalCredit = (Math.max(imageExtenderGenerateQuantity, 0) * IMAGE_EXTENDER_USE_CREDIT).toFixed(1);

                return {
                    list: [
                        [
                            { label: label1, value: value1 },
                            { label: label2, value: value2 },
                        ],
                    ],
                    totalCredit: totalCredit,
                };
            }
            case FJAppConstant.TEXT_TO_IMAGE: {
                let list = [];

                FJTextToImageDataConfig.MODELS.forEach(item => {
                    if (textToImageCurModel === item.value) {
                        const useCredit =
                            (item.creditPriceLevels && item.creditPriceLevels?.[textToImageResolution]) ||
                            item.useCredit ||
                            0;
                        list.push([
                            {
                                label: item.name,
                                value: `${FJLocalStore._(useCredit === 1 ? 'use-credit' : 'use-credits').replace('xxx', useCredit)}`,
                            },
                            {
                                label: 'count',
                                value: `${textToImageGenerateQuantity}`,
                            },
                        ]);
                    }
                });

                return {
                    list: list,
                    totalCredit: textToImageUseCredit,
                };
            }
            case FJAppConstant.AI_VIDEO_GENERATOR: {
                const currentModelInfo = AI_VIDEO_GENERATION_MODEL_LIST.find(
                    item => item.value === videoGeneratorCurrentModel,
                );

                const creditPrice =
                    (videoGeneratorActiveAiSFX && currentModelInfo?.creditPriceWidThAudio) ||
                    currentModelInfo?.creditPriceLevels?.[currentVideoResolution] ||
                    currentModelInfo?.creditPrice;
                const label1 = FJLocalStore._(currentModelInfo?.name || '');
                const value1 = `${creditPrice} ${FJLocalStore._('credits-s')}`;
                const label2 = 'duration';
                const value2 = `${videoGeneratorCurrentDuration}s`;

                const item2label1 = FJLocalStore._('ai-sfx');
                const item2value1 = `${AI_VIDEO_DUB_USE_CREDIT} ${FJLocalStore._('credits-s')}`;
                const item2label2 = 'duration';
                const item2value2 = `${videoGeneratorCurrentDuration}s`;

                let list = [
                    [
                        { label: label1, value: value1 },
                        { label: label2, value: value2 },
                    ],
                ];

                if (videoGeneratorActiveAiSFX && !currentModelInfo?.creditPriceWidThAudio) {
                    list.push([
                        { label: item2label1, value: item2value1 },
                        { label: item2label2, value: item2value2 },
                    ]);
                }
                let totalCredit =
                    creditPrice * videoGeneratorCurrentDuration +
                    (currentModelInfo?.creditPriceWidThAudio ? 0 : videoGeneratorAiSfxTotalCredit);
                if (tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_EFFECTS_VIDEO) {
                    list = [
                        [
                            {
                                label: FJLocalStore._(videoEffectStyleSelectId),
                                value: `15 ${FJLocalStore._('credits-s')}`,
                            },
                            { label: 'duration', value: `${Math.ceil(videoEffectUploadVideoInfo?.duration) || 1}s` },
                        ],
                    ];
                    totalCredit = videoEffectCredit;
                }

                return {
                    list,
                    totalCredit,
                };
            }
            case FJAppConstant.AI_AUDIO_GENERATOR: {
                const label1 = FJLocalStore._('ai-music-generator');
                const value1 = `${AI_AUDIO_USE_CREDIT} ${FJLocalStore._('credits-time')}`;
                const label2 = FJLocalStore._('current-consumption');
                const value2 = FJLocalStore._('1-time');

                return {
                    list: [
                        [
                            { label: label1, value: value1 },
                            { label: label2, value: value2 },
                        ],
                    ],
                    totalCredit: AI_AUDIO_USE_CREDIT,
                };
            }
            case FJAppConstant.TEXT_TO_FULL_VIDEO:
            case FJAppConstant.BLOG_URL_TO_VIDEO:
            case FJAppConstant.PRODUCT_URL_TO_VIDEO:
            case FJAppConstant.PPT_TO_VIDEO: {
                let textToImageLabel = FJLocalStore._('ai-text-to-image');
                let textToImageValue = `${TEXT_TO_VIDEO_PRICE.textToImage} ${FJLocalStore._('credit-image')}`;
                let stockLibraryLabel = FJLocalStore._('stock-library');
                let stockLibraryValue = `${TEXT_TO_VIDEO_PRICE.mediaMatch} ${FJLocalStore._('credits-time')}`;
                let textToSpeechLabel = FJLocalStore._('ai-text-to-speech');
                let textToSpeechValue = FJLocalStore._('credit-characters').replace(
                    'xxx',
                    TEXT_TO_VIDEO_PRICE.textToSpeech,
                );
                let productUrlToVideoLabel = FJLocalStore._('ai-product-url-to-video');
                let productUrlToVideoValue = `${TEXT_TO_VIDEO_PRICE.productUrlToVideo} ${FJLocalStore._('credits-time')}`;
                let pptToVideoLabel = FJLocalStore._('ai-ppt-to-video');
                let pptToVideoValue = `${TEXT_TO_VIDEO_PRICE.pptToVideo} ${FJLocalStore._('credits-time')}`;
                let list = [];
                switch (selectTab) {
                    case FJAppConstant.TEXT_TO_FULL_VIDEO:
                    case FJAppConstant.BLOG_URL_TO_VIDEO:
                        if (mediaSource === FJConstant.STOCK_LIBRARY) {
                            list.push({
                                label: stockLibraryLabel,
                                value: stockLibraryValue,
                            });
                        } else {
                            list.push({
                                label: textToImageLabel,
                                value: textToImageValue,
                            });
                        }
                        break;
                    case FJAppConstant.PRODUCT_URL_TO_VIDEO:
                        list.push({
                            label: productUrlToVideoLabel,
                            value: productUrlToVideoValue,
                        });
                        break;
                    case FJAppConstant.PPT_TO_VIDEO:
                        list.push({
                            label: pptToVideoLabel,
                            value: pptToVideoValue,
                        });
                        break;
                    default:
                        break;
                }
                if (voiceSettingActive) {
                    list.push({
                        label: textToSpeechLabel,
                        value: textToSpeechValue,
                    });
                }
                return {
                    list: [list],
                    totalCredit: textToVideoTotalCredit,
                };
            }

            case FJAppConstant.AI_VIDEO_DUB: {
                const label1 = FJLocalStore._('ai-sfx');
                const value1 = `${AI_VIDEO_DUB_USE_CREDIT} ${FJLocalStore._('credits-s')}`;
                const label2 = 'duration';
                const value2 = `${Math.floor(aiVideoDubTrimInfo.end - aiVideoDubTrimInfo.start)}s`;

                return {
                    list: [
                        [
                            { label: label1, value: value1 },
                            { label: label2, value: value2 },
                        ],
                    ],
                    totalCredit: aiVideoDubTotalCredit,
                };
            }

            case FJAppConstant.AI_TEXT_BASED_EDITING: {
                const duration = Math.ceil(textBasedEditingDuration / 60);

                const label1 = FJLocalStore._('transcribe');
                const value1 = `${FJTextBasedEditingConfig.unitPrice} ${FJLocalStore._('credits-minute')}`;
                const label2 = FJLocalStore._('duration');
                const value2 = `${duration} ${FJLocalStore._('minutes').toLowerCase()}`;

                return {
                    list: [
                        [
                            { label: label1, value: value1 },
                            { label: label2, value: value2 },
                        ],
                    ],
                    totalCredit: (duration * FJTextBasedEditingConfig.unitPrice).toFixed(1),
                };
            }

            case FJAppConstant.OBJECT_ERASER: {
                const label1 =
                    objectEraseCurMenuIndex === 0 ? FJLocalStore._('custom-removal') : FJLocalStore._('prompt-removal');
                const value1 = `${OBJECT_ERASE_USE_CREDIT} ${FJLocalStore._('credit-image')}`;
                const label2 = 'count';
                const value2 = 1;

                return {
                    list: [
                        [
                            { label: label1, value: value1 },
                            { label: label2, value: value2 },
                        ],
                    ],
                    totalCredit: OBJECT_ERASE_USE_CREDIT,
                };
            }
            case FJAppConstant.AI_PHOTO_EDITOR: {
                const currentModel = AI_PHOTO_EDITOR_MODELS.find(item => item.value === aiPhotoEditorCurModel);
                // const useCredit = currentModel?.useCredit || 0;
                const useCredit =
                    (currentModel?.creditPriceLevels && currentModel?.creditPriceLevels?.[aiPhotoEditorResolution]) ||
                    currentModel?.useCredit ||
                    0;
                const label1 = FJLocalStore._(currentModel?.name);
                const value1 = `${FJLocalStore._('use-credits').replace('xxx', useCredit)}`;
                const label2 = 'count';
                const value2 = 1;

                return {
                    list: [
                        [
                            { label: label1, value: value1 },
                            { label: label2, value: value2 },
                        ],
                    ],
                    totalCredit: useCredit,
                };
            }
            case FJAppConstant.HEADSHOT_GENERATOR: {
                const label1 = FJLocalStore._(headshotGeneratorCategory);
                const value1 =
                    headshotGeneratorCategory === 'professional'
                        ? FJLocalStore._('credits-per-set').replace(
                              'xxx',
                              headshotGeneratorUnitPrice * headshotGeneratorTotalOutput,
                          )
                        : FJLocalStore._('credits-per-style').replace('xxx', headshotGeneratorUnitPrice);
                const label2 = 'count';
                const value2 = headshotGeneratorTotalOutput;

                return {
                    list: [
                        [
                            { label: label1, value: value1 },
                            { label: label2, value: value2 },
                        ],
                    ],
                    totalCredit: headshotGeneratorUnitPrice * headshotGeneratorTotalOutput,
                };
            }
            default:
                return { list: [], totalCredit: 0 };
        }
    };

    const { list, totalCredit } = getDataList();

    return {
        list,
        totalCredit,
    };
};

export default useUsageDetailContainer;
